/**
 * Specification Field Definitions Hook
 * 
 * Manages the schema and metadata for specification fields. Field definitions
 * define the structure, validation rules, and display properties for fields
 * used in building product/service specifications.
 * 
 * **Field Type Support:**
 * - `text`: Short text input
 * - `textarea`: Long text input
 * - `number`: Numeric input with decimal support
 * - `dropdown`: Single selection from predefined options
 * - `multiselect`: Multiple selections from options
 * - `date`: Date picker input
 * - `boolean`: Yes/No toggle
 * - `file_upload`: File attachment
 * 
 * **Validation Rules:**
 * - **Number**: Min/max values, decimal places
 * - **Text**: Pattern matching (regex), length limits
 * - **Dropdown/Multiselect**: Allowed values list
 * - **Date**: Min/max date ranges
 * 
 * **Field Properties:**
 * - `field_name`: Unique identifier (system key)
 * - `field_label`: Display name shown to users
 * - `is_required`: Whether field must be filled
 * - `default_value`: Pre-populated value
 * - `help_text`: Guidance for users
 * - `unit_of_measure`: Units (kg, meters, volts, etc.)
 * - `display_order`: Ordering within category
 * - `is_active`: Soft delete flag
 * 
 * **Category Organization:**
 * - Fields are grouped under specification categories
 * - Categories can be filtered by category_id
 * - Hierarchical structure enables organized specs
 * 
 * **Key Features:**
 * - Fetch field definitions with optional category filter
 * - Create new field definitions
 * - Update existing field properties
 * - Delete fields (hard delete)
 * - Deactivate fields (soft delete)
 * - Include/exclude inactive fields
 * 
 * **Use Cases:**
 * - Specification template builder
 * - Dynamic form generation
 * - RFQ requirement definition
 * - Vendor capability specification
 * - Admin field management
 * 
 * @param {string} categoryId - Optional category ID to filter fields
 * @param {boolean} includeInactive - Whether to include inactive fields
 * 
 * @returns {Object} Field definitions state
 * @returns {SpecificationFieldDefinition[]} fields - Array of field definitions
 * @returns {boolean} loading - Loading state
 * @returns {string | null} error - Error message if operation failed
 * @returns {Function} createField - Create new field definition
 * @returns {Function} updateField - Update existing field
 * @returns {Function} deleteField - Permanently delete field
 * @returns {Function} deactivateField - Soft delete field
 * @returns {Function} refetch - Manually refresh fields
 * 
 * @example
 * // Fetch all active fields for a category
 * const { fields, loading } = useSpecificationFieldDefinitions(categoryId);
 * 
 * fields.map(field => (
 *   <FormField
 *     key={field.id}
 *     label={field.field_label}
 *     type={field.field_type}
 *     required={field.is_required}
 *     helpText={field.help_text}
 *   />
 * ));
 * 
 * @example
 * // Create number field with validation
 * const { createField } = useSpecificationFieldDefinitions();
 * 
 * await createField({
 *   category_id: categoryId,
 *   field_name: 'voltage',
 *   field_label: 'Operating Voltage',
 *   field_type: 'number',
 *   is_required: true,
 *   unit_of_measure: 'V',
 *   validation_rules: {
 *     min: 100,
 *     max: 240,
 *     decimalPlaces: 1
 *   },
 *   display_order: 10
 * });
 * 
 * @example
 * // Create dropdown field
 * await createField({
 *   category_id: categoryId,
 *   field_name: 'color',
 *   field_label: 'Color',
 *   field_type: 'dropdown',
 *   validation_rules: {
 *     allowedValues: ['Red', 'Blue', 'Green', 'Yellow']
 *   },
 *   display_order: 20
 * });
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import type { Json } from '@/integrations/supabase/types';

export type FieldType = 'text' | 'number' | 'dropdown' | 'multiselect' | 'date' | 'boolean' | 'textarea' | 'file_upload';

export interface ValidationRules {
  min?: number;
  max?: number;
  pattern?: string;
  allowedValues?: string[];
  decimalPlaces?: number;
  minDate?: string;
  maxDate?: string;
}

export interface SpecificationFieldDefinition {
  id: string;
  public_id: string;
  category_id: string;
  field_name: string;
  field_label: string;
  field_type: FieldType;
  is_required: boolean;
  validation_rules: Json;
  default_value: string | null;
  help_text: string | null;
  unit_of_measure: string | null;
  display_order: number;
  is_active: boolean;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

interface CreateFieldData {
  category_id: string;
  field_name: string;
  field_label: string;
  field_type: FieldType;
  is_required?: boolean;
  validation_rules?: Json;
  default_value?: string;
  help_text?: string;
  unit_of_measure?: string;
  display_order: number;
}

interface UpdateFieldData {
  field_name?: string;
  field_label?: string;
  field_type?: FieldType;
  is_required?: boolean;
  validation_rules?: Json;
  default_value?: string;
  help_text?: string;
  unit_of_measure?: string;
  display_order?: number;
  is_active?: boolean;
}

interface UseFieldDefinitionsOptions {
  categoryId?: string;
  includeInactive?: boolean;
}

export const useSpecificationFieldDefinitions = (options: UseFieldDefinitionsOptions = {}) => {
  const { categoryId, includeInactive = false } = options;
  const [fields, setFields] = useState<SpecificationFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFields = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('specification_field_definitions')
        .select('*')
        .order('display_order', { ascending: true });

      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      if (!includeInactive) {
        query = query.eq('is_active', true);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;
      setFields(data || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch field definitions';
      setError(message);
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, [categoryId, includeInactive]);

  const createField = async (fieldData: CreateFieldData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: createError } = await supabase
        .from('specification_field_definitions')
        .insert({
          ...fieldData,
          created_by_user_id: user.id,
        })
        .select()
        .single();

      if (createError) throw createError;

      toast({
        title: 'Success',
        description: 'Field definition created successfully',
      });

      await fetchFields();
      return data;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create field definition';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const updateField = async (id: string, updates: UpdateFieldData) => {
    try {
      const { error: updateError } = await supabase
        .from('specification_field_definitions')
        .update(updates)
        .eq('id', id);

      if (updateError) throw updateError;

      toast({
        title: 'Success',
        description: 'Field definition updated successfully',
      });

      await fetchFields();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update field definition';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deleteField = async (id: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('specification_field_definitions')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      toast({
        title: 'Success',
        description: 'Field definition deleted successfully',
      });

      await fetchFields();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete field definition';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
      throw err;
    }
  };

  const deactivateField = async (id: string) => {
    await updateField(id, { is_active: false });
  };

  return {
    fields,
    loading,
    error,
    createField,
    updateField,
    deleteField,
    deactivateField,
    refetch: fetchFields,
  };
};
